<template>
    <div class="dk-popper d--inline-block">
        <div
            v-show="!disabled && tooltipShowed"
            ref="tooltip"
            :class="{
                'dk-popper__tooltip': true,
                [`dk-popper__tooltip--${_uid}`]: true,
                [`dk-popper__tooltip--has-arrow`]: hasArrow,
            }"
            v-on="tooltipEvents"
        >
            <div class="dk-popper__tooltip-inner">
                <div :style="{ 'max-width': maxWidth, 'max-height': maxHeight }" class="dk-popper__tooltip-content">
                    <slot>
                        <div v-html="content"></div>
                    </slot>
                </div>
            </div>
        </div>

        <div ref="reference" class="dk-popper__reference d--inline-block" v-on="referenceEvents">
            <slot name="reference">
                <button :class="`btn btn--${buttonVariant}`" type="button">
                    {{ btnText }}
                    <icon
                        v-if="hasSelectIcon"
                        name="generic-select"
                        size="xs"
                        color="grey-700"
                        class="m-left--xs"
                    />
                </button>
            </slot>
        </div>
    </div>
</template>

<script>
import { on, off } from '@v4/common/utils/dom';

/**
 * A dropdown/popover component which is wrapping Popper.js
 *
 * @version 4.0.0
 * @see [Popper.js](https://popper.js.org/)
 * @example ./examples/DkPopper.md
 */
export default {
    name: 'DkPopper',
    components: {
        Icon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/Icon.vue'),
        }),
    },
    props: {
        /**
         * Will be shown only when there isn't a default slot. HTML markups are allowed.
         */
        content: {
            type: String,
            default: '',
        },
        /**
         * @see [Popper Options](https://popper.js.org/popper-documentation.html).
         */
        popperOpts: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Optional value: hover or click.
         */
        trigger: {
            type: String,
            default: 'click',
            validator: (value) => ['click', 'hover'].indexOf(value) > -1,
        },
        /**
         * Delay in ms before showing tooltip during a mouse over.
         */
        delayOnMouseOver: {
            type: Number,
            default: 10,
        },
        /**
         * Delay in ms before hiding tooltip during a mouse out.
         */
        delayOnMouseOut: {
            type: Number,
            default: 10,
        },
        /**
         * Append tooltip content to the body element.
         */
        appendToBody: {
            type: Boolean,
            default: false,
        },
        /**
         * Do not show the tooltip.
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Enable or disable the tooltip arrow.
         */
        hasArrow: {
            type: Boolean,
            default: true,
        },
        /**
         * Show select icon in the button
         */
        hasSelectIcon: {
            type: Boolean,
            default: true,
        },
        /**
         * Max tooltip width.
         */
        maxWidth: {
            type: String,
            default: '220px',
        },
        /**
         * Max tooltip height.
         */
        maxHeight: {
            type: String,
            default: '180px',
        },
        /**
         * Button text
         */
        buttonText: {
            type: String,
            default: 'Dropdown',
        },
        /**
         * Button variant: primary and secondary
         */
        buttonVariant: {
            type: String,
            default: 'tertiary',
        },
        /**
         * Make the button selectable
         */
        selectable: {
            type: Boolean,
            default: false,
        },
        /**
         * Make the selection multiple
         */
        multiple: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            btnText: '',
            popperJS: null,
            tooltipShowed: false,
            appendedToBody: false,
            popperOptions: {
                placement: 'bottom',
                computeStyle: {
                    gpuAcceleration: false,
                },
            },
        };
    },
    computed: {
        referenceElm() {
            return this.$refs.reference;
        },
        tooltipElm() {
            return this.$refs.tooltip;
        },
        isTriggerClick() {
            return this.trigger === 'click';
        },
        events() {
            return {
                mouseover: this.onMouseOver,
                focus: this.onMouseOver,
                mouseout: this.onMouseOut,
                blur: this.onMouseOut,
            };
        },
        tooltipEvents() {
            if (this.isTriggerClick) {
                return {};
            }

            return this.events;
        },
        referenceEvents() {
            if (this.isTriggerClick) {
                return {
                    click: this.onClick,
                };
            }

            return this.events;
        },
    },
    watch: {
        tooltipShowed(showed) {
            if (showed) {
                /**
                 * This event is fired when the showTooltip instance method is called.
                 * @event show
                 * @type {undefined}
                 */
                this.$emit('show');
                document.getElementsByTagName('body')[0].classList.add('dk-popper-tooltip-visible');
                this.updateTooltip();
            } else {
                /**
                 * This event is fired immediately when the hideTooltip instance method has been called.
                 * @event hide
                 * @type {undefined}
                 */
                this.$emit('hide');
                document.getElementsByTagName('body')[0].classList.remove('dk-popper-tooltip-visible');
                this.destroyTooltip();
            }
        },
    },
    created() {
        this.popperOptions = { ...this.popperOptions, ...this.popperOpts };
        this.btnText = this.buttonText;
    },
    destroyed() {
        this.removeDocumentClickEvent();
        this.hideTooltip();
        this.destroyTooltip();
    },
    methods: {
        toggleTooltip() {
            this.tooltipShowed = !this.tooltipShowed;
        },
        hideTooltip() {
            this.tooltipShowed = false;
        },
        showTooltip() {
            this.tooltipShowed = true;
        },
        onClick() {
            this.toggleTooltip();

            if (!this.popperJS) {
                on(document, 'click', this.handleDocumentClick);
            }
        },
        updateTooltip() {
            this.popperJS ? this.popperJS.scheduleUpdate() : this.createTooltip();
        },
        destroyTooltip() {
            if (this.tooltipShowed) {
                return;
            }

            if (this.popperJS) {
                this.popperJS.destroy();
                this.popperJS = null;
            }

            this.removeDocumentClickEvent();

            if (this.appendedToBody) {
                this.appendedToBody = false;
                document.body.removeChild(this.tooltipElm);
            }
        },
        createTooltip() {
            this.$nextTick(async () => {
                if (this.appendToBody && !this.appendedToBody) {
                    this.appendedToBody = true;
                    document.body.appendChild(this.tooltipElm);
                }

                if (this.popperJS && this.popperJS.destroy) {
                    this.popperJS.destroy();
                }

                /**
                 * This event is fired when the tooltip is created.
                 * @event created
                 * @type {object} Popper
                 */
                this.popperOptions.onCreate = (data) => this.$emit('created', data);

                /**
                 * This event is fired when the tooltip is updated.
                 * @event updated
                 * @type {object} Popper
                 */
                this.popperOptions.onUpdate = (data) => this.$emit('updated', data);

                const Popper = (await import('popper.js')).default;
                this.popperJS = new Popper(this.referenceElm, this.tooltipElm, this.popperOptions);
            });
        },
        removeDocumentClickEvent() {
            if (this.isTriggerClick) {
                off(document, 'click', this.handleDocumentClick);
            }
        },
        handleDocumentClick(e) {
            const { target } = e;

            if (
                !this.$el ||
                !this.referenceElm ||
                this.$el.contains(target) ||
                this.referenceElm.contains(target) ||
                !this.tooltipElm ||
                this.tooltipElm.contains(target)
            ) {
                return;
            }

            /**
             * This event is fired only when clicking outside from the tooltip and the trigger value is 'click'.
             *
             * @event document-click
             * @type {object} HTML DOM Event Object
             */
            this.$emit('document-click', { e });
            this.hideTooltip();
        },
        clearTimeout() {
            clearTimeout(this._timer);
        },
        onMouseOver() {
            this.clearTimeout();
            this._timer = setTimeout(() => this.showTooltip(), this.delayOnMouseOver);
        },
        onMouseOut() {
            this.clearTimeout();
            this._timer = setTimeout(() => this.hideTooltip(), this.delayOnMouseOut);
        },
    },
};
</script>
